import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  IconButton,
  TextField,
  Snackbar,
  Alert,
  Backdrop,
} from "@mui/material";
import { Close, Email, CardGiftcard } from "@mui/icons-material";
import { motion } from "framer-motion";
import { trackJourneyStep } from "../utils/advancedAnalytics";
import { trackButtonClick } from "../utils/analytics";

interface EmailCapturePopupProps {
  onClose: () => void;
  onEmailSubmit: (email: string) => void;
}

const EmailCapturePopup: React.FC<EmailCapturePopupProps> = ({
  onClose,
  onEmailSubmit,
}) => {
  const [email, setEmail] = useState("");
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !email.includes("@")) {
      setSnackbarMessage("Please enter a valid email address");
      setSnackbarSeverity("error");
      setShowSnackbar(true);
      return;
    }

    trackJourneyStep("email_captured_popup", {
      email,
      source: "popup_modal",
    });

    trackButtonClick("popup_email_submit", {
      button_text: "Get Free Pack",
      email,
    });

    onEmailSubmit(email);
    setSnackbarMessage("Thank you! Your free activity pack is on its way! 🎉");
    setSnackbarSeverity("success");
    setShowSnackbar(true);

    // Close popup after success
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  const handleClose = () => {
    trackJourneyStep("popup_dismissed", {
      source: "popup_modal",
    });
    onClose();
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  return (
    <>
      <Backdrop
        open={true}
        sx={{
          zIndex: 1300,
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          backdropFilter: "blur(4px)",
        }}
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 50 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          onClick={(e) => e.stopPropagation()}
          style={{
            maxWidth: "90vw",
            width: "400px",
          }}
        >
          <Card
            sx={{
              p: 4,
              background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
              color: "white",
              borderRadius: 4,
              boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
              border: "1px solid rgba(255,255,255,0.2)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background:
                  "radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)",
                pointerEvents: "none",
              },
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleClose}
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: "white",
                bgcolor: "rgba(255,255,255,0.2)",
                "&:hover": {
                  bgcolor: "rgba(255,255,255,0.3)",
                },
              }}
              size="small"
            >
              <Close />
            </IconButton>

            {/* Header */}
            <Box sx={{ textAlign: "center", mb: 3, position: "relative" }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  bgcolor: "rgba(255,255,255,0.2)",
                  mb: 2,
                }}
              >
                <CardGiftcard sx={{ fontSize: 30, color: "white" }} />
              </Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  mb: 1,
                  textShadow: "0 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Get Your Free Activity Pack
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  opacity: 0.9,
                  fontSize: "1.1rem",
                  lineHeight: 1.4,
                }}
              >
                Join 15,000+ dads and get 10 premium activities instantly
              </Typography>
            </Box>

            {/* Form */}
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{ position: "relative" }}
            >
              <TextField
                fullWidth
                type="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  mb: 3,
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "rgba(255,255,255,0.95)",
                    borderRadius: 2,
                    "& fieldset": {
                      borderColor: "rgba(255,255,255,0.3)",
                    },
                    "&:hover fieldset": {
                      borderColor: "rgba(255,255,255,0.5)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "white",
                    },
                    "& input": {
                      color: "#333",
                      fontSize: "1rem",
                      "&::placeholder": {
                        color: "#666",
                        opacity: 1,
                      },
                    },
                  },
                }}
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: "#666" }} />,
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{
                  bgcolor: "white",
                  color: "#FF6B6B",
                  fontWeight: 700,
                  py: 1.5,
                  fontSize: "1.1rem",
                  borderRadius: 2,
                  textTransform: "none",
                  boxShadow: "0 8px 25px rgba(255,255,255,0.3)",
                  "&:hover": {
                    bgcolor: "#FAFAFA",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(255,255,255,0.4)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Get Free Pack
              </Button>

              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  mt: 2,
                  opacity: 0.8,
                  fontSize: "0.8rem",
                }}
              >
                🔒 No spam, unsubscribe anytime
              </Typography>
            </Box>
          </Card>
        </motion.div>
      </Backdrop>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailCapturePopup;
